import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Sparkles, X } from 'lucide-react';
import { newsPagePath } from '../../constants/news.constants';
import { usePrefersReducedMotion } from '../../hooks/useDialogA11y';
import type { NewsItem, NewsPlatform } from '../../types/news.types';

/** Durée d'affichage d'une nouveauté avant de passer à la suivante. Volontairement lente. */
const ROTATION_INTERVAL_MS = 8000;

const ICON_BUTTON =
  'tw-inline-flex tw-items-center tw-justify-center tw-w-7 tw-h-7 tw-rounded tw-border-0 tw-bg-transparent tw-text-affinicar-secondary hover:tw-bg-affinicar-light/40 focus-visible:tw-outline focus-visible:tw-outline-2 focus-visible:tw-outline-affinicar-secondary tw-transition-colors tw-flex-shrink-0';

/**
 * Masquage du bandeau : `sessionStorage`, donc pour la session du navigateur
 * uniquement et RIEN en base. Le bandeau revient à la session suivante tant que
 * les nouveautés ne sont pas lues. Le stockage peut être indisponible
 * (navigation privée stricte) : le masquage ne vaut alors que jusqu'au
 * rechargement.
 */
function dismissalKey(platform: NewsPlatform, viewerId: string): string {
  return `news-banner-dismissed:${platform}:${viewerId}`;
}

function readDismissed(key: string): boolean {
  try {
    return window.sessionStorage.getItem(key) === '1';
  } catch {
    return false;
  }
}

function writeDismissed(key: string): void {
  try {
    window.sessionStorage.setItem(key, '1');
  } catch {
    // Stockage indisponible : le masquage reste en mémoire.
  }
}

/**
 * Bandeau des nouveautés « bandeau » non lues : une à la fois, en rotation
 * automatique lente, mise en pause au survol ou quand le focus est dedans, et
 * désactivée si l'utilisateur a demandé moins d'animations (les flèches restent).
 * Cliquer ouvre la page de la nouveauté ; la croix masque le bandeau pour la
 * session.
 *
 * Le parent doit le monter avec une `key` par lecteur : l'état de masquage est
 * lu une fois, au montage.
 */
export function NewsBanner({
  platform,
  viewerId,
  items,
}: {
  platform: NewsPlatform;
  viewerId: string;
  items: NewsItem[];
}) {
  const storageKey = dismissalKey(platform, viewerId);
  const [dismissed, setDismissed] = useState(() => readDismissed(storageKey));
  const [position, setPosition] = useState(0);
  const [paused, setPaused] = useState(false);
  const reducedMotion = usePrefersReducedMotion();

  const count = items.length;
  const rotates = !dismissed && !paused && !reducedMotion && count > 1;

  // Effet pur : minuterie de rotation, arrêtée au démontage et à chaque pause.
  useEffect(() => {
    if (!rotates) return undefined;
    const timer = window.setInterval(() => {
      setPosition((current) => current + 1);
    }, ROTATION_INTERVAL_MS);
    return () => window.clearInterval(timer);
  }, [rotates]);

  if (dismissed || count === 0) return null;

  // La liste rétrécit quand une nouveauté vient d'être lue : la position reste valable.
  const index = ((position % count) + count) % count;
  const item = items[index];

  return (
    <div
      role="region"
      aria-label="Nouveautés"
      className="tw-flex-shrink-0 tw-bg-affinicar-lighter tw-border-b tw-border-affinicar-light tw-px-4 md:tw-px-6 tw-py-2"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
    >
      <div className="tw-flex tw-items-center tw-gap-2">
        <Link
          key={item.id}
          to={newsPagePath(item.slug)}
          className="tw-flex-1 tw-min-w-0 tw-flex tw-items-center tw-gap-2 tw-no-underline tw-text-affinicar-dark hover:tw-text-affinicar-secondary tw-animate-fade-in motion-reduce:tw-animate-none"
        >
          <Sparkles className="tw-w-4 tw-h-4 tw-flex-shrink-0 tw-text-affinicar-secondary" aria-hidden="true" />
          <span className="tw-flex-shrink-0 tw-text-[11px] tw-font-semibold tw-uppercase tw-tracking-wide tw-text-white tw-bg-affinicar-secondary tw-rounded tw-px-1.5 tw-py-0.5">
            Nouveauté
          </span>
          <span className="tw-text-sm tw-font-semibold tw-truncate">{item.title}</span>
          {item.shortDescription && (
            <span className="tw-hidden lg:tw-inline tw-text-sm tw-text-gray-600 tw-truncate tw-min-w-0 tw-flex-1">
              {item.shortDescription}
            </span>
          )}
          <span className="tw-flex-shrink-0 tw-text-sm tw-font-medium tw-underline tw-hidden sm:tw-inline">
            En savoir plus
          </span>
        </Link>

        {count > 1 && (
          <div className="tw-flex tw-items-center tw-gap-0.5 tw-flex-shrink-0">
            <button
              type="button"
              className={ICON_BUTTON}
              aria-label="Nouveauté précédente"
              onClick={() => setPosition((current) => current - 1)}
            >
              <ChevronLeft className="tw-w-4 tw-h-4" />
            </button>
            <span className="tw-text-xs tw-text-gray-600 tw-tabular-nums" aria-live="off">
              {index + 1}/{count}
            </span>
            <button
              type="button"
              className={ICON_BUTTON}
              aria-label="Nouveauté suivante"
              onClick={() => setPosition((current) => current + 1)}
            >
              <ChevronRight className="tw-w-4 tw-h-4" />
            </button>
          </div>
        )}

        <button
          type="button"
          className={ICON_BUTTON}
          aria-label="Masquer le bandeau des nouveautés"
          onClick={() => {
            writeDismissed(storageKey);
            setDismissed(true);
          }}
        >
          <X className="tw-w-4 tw-h-4" />
        </button>
      </div>
    </div>
  );
}
